import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { 
  ShoppingBag, 
  Sparkles, 
  PhoneCall, 
  Check, 
  ShieldCheck, 
  Truck, 
  Layers, 
  Box, 
  Ruler, 
  Info,
  ChevronRight,
  MessageCircle
} from "lucide-react";
import { toast } from "sonner";
import apiClient, { resolveImageUrl } from "@/utils/api";
import ReviewsSection from "@/components/ReviewsSection";
import { useCart } from "@/context/CartContext";
import { useSiteSettings } from "@/context/SiteSettingsContext";
import ProductCard from "@/components/ProductCard";
import SetBreakdownModal from "@/components/SetBreakdownModal";

export default function ProductDetail() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { settings } = useSiteSettings();

  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState("");
  const [selectedColor, setSelectedColor] = useState("");
  const [selectedDesign, setSelectedDesign] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("set_info"); // "set_info", "wholesale", "delivery"

  useEffect(() => {
    fetchProduct();
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [productId]);

  const fetchProduct = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get(`/products/${productId}`);
      if (data && data.product) {
        setProduct(data.product);
        setRelated(data.related || []);
        setActiveImage(data.product.images?.[0] || "");
        setSelectedColor(data.product.available_colors?.[0] || "Standard Set Pack");
        setSelectedDesign(data.product.design_code || "");
      }
    } catch (err) {
      console.error("Product load error", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-24 text-center text-stone-400 text-sm">
        Loading lace details...
      </div>
    );
  }

  if (!product) {
    return (
      <div className="max-w-xl mx-auto px-4 py-24 text-center space-y-4">
        <h2 className="font-heading text-2xl font-bold">Product not found</h2>
        <Link to="/shop" className="px-5 py-2 bg-[#1C1917] text-white text-xs font-semibold rounded-lg">
          Back to Shop
        </Link>
      </div>
    );
  }

  const completeSet = product.complete_set || {};
  const totalGaz = Number(completeSet.total_gaz)
    || (Number(completeSet.taan_count) * Number(completeSet.gaz_per_taan))
    || 0;

  const handleAddToCart = () => {
    addToCart(product, quantity, selectedColor, selectedDesign);
  };

  const handleBuyNow = () => {
    addToCart(product, quantity, selectedColor, selectedDesign);
    navigate("/checkout");
  };

  const handleWhatsAppOrder = () => {
    const phone = (settings.whatsapp_number || "").replace(/[^0-9]/g, "");
    if (!phone) {
      toast.error("WhatsApp number not configured yet.");
      return;
    }
    const lines = [
      "Assalam-o-Alaikum!",
      "I want to order this lace set from Anwer Fancy Lace:",
      "",
      `Product: ${product.name}`,
      `Category: ${product.category}`,
    ];
    if (product.design_code) lines.push(`Design Code: ${product.design_code}`);
    lines.push(
      `Quantity: ${quantity} Complete Set${quantity === 1 ? "" : "s"}`,
      `Total: Rs. ${(product.retail_price * quantity).toLocaleString()}`,
      "",
      "Please confirm availability and dispatch."
    );
    const msg = encodeURIComponent(lines.join("\n"));
    window.open(`https://wa.me/${phone}?text=${msg}`, "_blank");
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-12">
      
      {/* Breadcrumb Navigation */}
      <div className="flex items-center gap-2 text-xs text-stone-500">
        <Link to="/" className="hover:text-stone-800">Home</Link>
        <ChevronRight className="w-3 h-3" />
        <Link to="/shop" className="hover:text-stone-800">Shop</Link>
        <ChevronRight className="w-3 h-3" />
        <Link to={`/shop?category=${encodeURIComponent(product.category)}`} className="hover:text-stone-800">
          {product.category}
        </Link>
        <ChevronRight className="w-3 h-3" />
        <span className="text-stone-900 font-semibold truncate max-w-xs">{product.name}</span>
      </div>

      {/* Main Product Presentation */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        
        {/* Left: High-Res Image Gallery (5 cols) */}
        <div className="lg:col-span-5 space-y-4">
          <div className="relative aspect-square rounded-2xl overflow-hidden border border-[#E5E0D8] bg-[#FAF8F5] shadow-luxury">
            <img
              src={resolveImageUrl(activeImage || product.images?.[0])}
              alt={product.name}
              data-testid="product-detail-main-image"
              className="w-full h-full object-cover object-center"
            />
            {product.is_complete_set && (
              <div className="absolute top-4 left-4 bg-[#1C1917]/90 backdrop-blur-sm text-[#E6C687] text-xs font-bold px-3 py-1.5 rounded-lg border border-[#D4AF37]/50 shadow-md flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5 text-[#D4AF37]" />
                Complete Set Pack
              </div>
            )}
          </div>

          {/* Thumbnails */}
          {product.images && product.images.length > 1 && (
            <div className="flex items-center gap-3 overflow-x-auto pb-2">
              {product.images.map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveImage(img)}
                  className={`w-20 h-20 rounded-xl overflow-hidden border-2 shrink-0 transition ${
                    activeImage === img ? "border-[#D4AF37] ring-2 ring-[#D4AF37]/30" : "border-stone-200 opacity-70 hover:opacity-100"
                  }`}
                >
                  <img src={resolveImageUrl(img)} alt={`Thumb ${idx}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Right: Product Details & Set Breakdown (7 cols) */}
        <div className="lg:col-span-7 space-y-6">
          
          <div>
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-bold uppercase tracking-widest text-[#996515]">
                {product.category}
              </span>
              <span className="text-xs px-2.5 py-1 bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-full font-semibold">
                {product.stock_status || "In Stock"}
              </span>
            </div>

            <h1 
              data-testid="product-detail-name"
              className="font-heading text-2xl sm:text-3xl font-bold text-stone-900 mt-2 leading-tight"
            >
              {product.name}
            </h1>

            {product.sku && (
              <p className="text-xs text-stone-400 mt-1">SKU: {product.sku}</p>
            )}
          </div>

          {/* Price Box */}
          <div className="bg-[#FAF8F5] p-5 rounded-2xl border border-[#E5E0D8] flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <span className="text-xs text-stone-500 block">Retail Price (Complete Set Pack)</span>
              <div className="flex items-baseline gap-2">
                <span 
                  data-testid="product-detail-retail-price"
                  className="font-heading text-3xl font-extrabold text-stone-900"
                >
                  Rs. {product.retail_price.toLocaleString()}
                </span>
                <span className="text-xs text-stone-500 font-medium">PKR</span>
              </div>
            </div>

            {product.wholesale_price && (
              <div className="bg-white px-4 py-2.5 rounded-xl border border-[#D4AF37]/50 text-right">
                <span className="text-[11px] text-[#996515] uppercase font-bold block">Wholesale Rate</span>
                <span className="text-lg font-bold text-[#996515]">
                  Rs. {product.wholesale_price.toLocaleString()}
                </span>
                <span className="text-[10px] text-stone-400 block">Min. {product.min_wholesale_qty || 5} Sets</span>
              </div>
            )}
          </div>

          {/* Complete Set Summary Box (from admin product data only) */}
          {(completeSet.total_colors || completeSet.taan_count || totalGaz) ? (
            <div
              data-testid="product-complete-set-summary"
              className="bg-white p-5 rounded-2xl border-2 border-[#D4AF37]/50 space-y-3 shadow-sm"
            >
              <div className="flex items-center justify-between flex-wrap gap-2">
                <h3 className="font-heading text-sm font-bold text-stone-900 flex items-center gap-1.5">
                  <Layers className="w-4 h-4 text-[#D4AF37]" />
                  Complete Set
                </h3>
                {totalGaz ? (
                  <span className="text-xs font-semibold text-[#996515] bg-[#FAF8F5] px-2.5 py-1 rounded-md border border-[#E5E0D8]">
                    Total: {totalGaz} Gaz
                  </span>
                ) : null}
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs text-stone-700 pt-1">
                {completeSet.total_colors ? (
                  <div className="bg-[#FAF8F5] p-2 rounded-lg border border-stone-200">
                    <div className="text-[10px] uppercase text-stone-500">Colors</div>
                    <div className="font-bold text-stone-900">{completeSet.total_colors}</div>
                  </div>
                ) : null}
                {completeSet.taan_count ? (
                  <div className="bg-[#FAF8F5] p-2 rounded-lg border border-stone-200">
                    <div className="text-[10px] uppercase text-stone-500">Taan</div>
                    <div className="font-bold text-stone-900">{completeSet.taan_count}</div>
                  </div>
                ) : null}
                {completeSet.gaz_per_taan ? (
                  <div className="bg-[#FAF8F5] p-2 rounded-lg border border-stone-200">
                    <div className="text-[10px] uppercase text-stone-500">Gaz per Taan</div>
                    <div className="font-bold text-stone-900">{completeSet.gaz_per_taan}</div>
                  </div>
                ) : null}
                <div className="bg-[#FAF8F5] p-2 rounded-lg border border-stone-200">
                  <div className="text-[10px] uppercase text-stone-500">Minimum Order</div>
                  <div className="font-bold text-stone-900">{completeSet.min_order_sets || 1} Set{(completeSet.min_order_sets || 1) === 1 ? "" : "s"}</div>
                </div>
              </div>

              <p className="text-[11px] text-stone-500 italic pt-1">
                Sold only as a complete set. Individual colors, Taan or Gaz cannot be purchased separately.
              </p>
            </div>
          ) : (
            <div className="bg-[#FAF8F5] p-4 rounded-2xl border border-dashed border-stone-300 text-[11px] italic text-stone-500">
              Complete set details will be shown here once admin enters them.
            </div>
          )}

          {/* Color Selector */}
          {product.available_colors && product.available_colors.length > 0 && (
            <div className="space-y-2">
              <label className="text-xs font-bold uppercase tracking-wider text-stone-800 flex items-center justify-between">
                <span>Select Color: <span className="text-[#996515] font-semibold">{selectedColor}</span></span>
              </label>
              <div className="flex items-center gap-2 flex-wrap">
                {product.available_colors.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setSelectedColor(c)}
                    data-testid={`color-select-${c.replace(/\s+/g, '-').toLowerCase()}`}
                    className={`px-3.5 py-1.5 rounded-lg text-xs font-medium border transition ${
                      selectedColor === c
                        ? "bg-[#1C1917] text-white border-[#1C1917] shadow-sm font-semibold"
                        : "bg-white text-stone-700 border-stone-300 hover:border-stone-400"
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Design Code (single, admin-entered) */}
          {product.design_code ? (
            <div className="space-y-1">
              <label className="text-xs font-bold uppercase tracking-wider text-stone-800">
                Design Code:
              </label>
              <div
                data-testid="product-design-code"
                className="inline-block px-3.5 py-1.5 rounded-lg text-xs font-semibold bg-[#996515] text-white"
              >
                {product.design_code}
              </div>
            </div>
          ) : null}

          {/* Quantity and Actions */}
          <div className="space-y-4 pt-2">
            <div className="flex items-center gap-4">
              <span className="text-xs font-bold uppercase tracking-wider text-stone-700">Set Quantity:</span>
              <div className="flex items-center border border-stone-300 rounded-lg bg-white overflow-hidden">
                <button
                  type="button"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="px-3 py-1.5 text-stone-600 hover:bg-stone-100 text-sm font-bold"
                >
                  -
                </button>
                <span className="px-4 py-1.5 text-xs font-bold text-stone-900">{quantity} Sets</span>
                <button
                  type="button"
                  onClick={() => setQuantity(quantity + 1)}
                  className="px-3 py-1.5 text-stone-600 hover:bg-stone-100 text-sm font-bold"
                >
                  +
                </button>
              </div>
              <span className="text-xs text-stone-500">
                (Total: <span className="font-bold text-stone-900">Rs. {(product.retail_price * quantity).toLocaleString()}</span>)
              </span>
            </div>

            {/* Action Buttons Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <button
                type="button"
                onClick={handleAddToCart}
                data-testid="detail-add-to-cart-btn"
                className="w-full py-3.5 px-4 bg-[#1C1917] hover:bg-[#D4AF37] text-white hover:text-[#1C1917] text-xs font-bold rounded-xl shadow-md transition flex items-center justify-center gap-2"
              >
                <ShoppingBag className="w-4 h-4" />
                <span>Add Set to Cart</span>
              </button>

              <button
                type="button"
                onClick={handleBuyNow}
                data-testid="detail-buy-now-btn"
                className="w-full py-3.5 px-4 bg-[#D4AF37] hover:bg-[#B8860B] text-[#1C1917] text-xs font-bold rounded-xl shadow-md transition flex items-center justify-center gap-1.5"
              >
                <span>Buy Now (COD)</span>
              </button>

              <button
                type="button"
                onClick={handleWhatsAppOrder}
                data-testid="detail-whatsapp-order-btn"
                className="w-full py-3.5 px-4 bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-bold rounded-xl shadow-md transition flex items-center justify-center gap-2"
              >
                <MessageCircle className="w-4 h-4" />
                <span>WhatsApp Order</span>
              </button>
            </div>
          </div>

          {/* Value Highlights */}
          <div className="grid grid-cols-3 gap-3 pt-4 border-t border-[#E5E0D8] text-[11px] text-stone-600">
            <div className="flex items-center gap-2">
              <Truck className="w-4 h-4 text-[#D4AF37] shrink-0" />
              <span>Cash on Delivery (Pakistan)</span>
            </div>
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-[#D4AF37] shrink-0" />
              <span>100% Original China Lace</span>
            </div>
            <div className="flex items-center gap-2">
              <Box className="w-4 h-4 text-[#D4AF37] shrink-0" />
              <span>Complete Set Pack</span>
            </div>
          </div>

        </div>
      </div>

      {/* Tabs: Specifications, Wholesale, Delivery */}
      <div className="bg-white rounded-2xl border border-[#E5E0D8] p-6 sm:p-8 space-y-6">
        <div className="flex border-b border-[#E5E0D8] gap-6 text-sm font-semibold">
          <button
            onClick={() => setActiveTab("set_info")}
            className={`pb-3 border-b-2 transition ${
              activeTab === "set_info" ? "border-[#D4AF37] text-[#996515]" : "border-transparent text-stone-500 hover:text-stone-800"
            }`}
          >
            Product Description & Yardage
          </button>
          <button
            onClick={() => setActiveTab("wholesale")}
            className={`pb-3 border-b-2 transition ${
              activeTab === "wholesale" ? "border-[#D4AF37] text-[#996515]" : "border-transparent text-stone-500 hover:text-stone-800"
            }`}
          >
            Wholesale & Bulk Inquiries
          </button>
          <button
            onClick={() => setActiveTab("delivery")}
            className={`pb-3 border-b-2 transition ${
              activeTab === "delivery" ? "border-[#D4AF37] text-[#996515]" : "border-transparent text-stone-500 hover:text-stone-800"
            }`}
          >
            Payment & Nationwide Delivery
          </button>
        </div>

        {activeTab === "set_info" && (
          <div className="space-y-5 text-xs sm:text-sm text-stone-700 leading-relaxed max-w-4xl">
            {/* Preserves admin-entered whitespace, newlines, and simple headings */}
            <div
              data-testid="product-description"
              className="whitespace-pre-wrap text-stone-700"
            >
              {product.description}
            </div>

            {(completeSet.total_colors || completeSet.taan_count || completeSet.gaz_per_taan || totalGaz) ? (
              <div
                data-testid="product-complete-set-details"
                className="bg-[#FAF8F5] p-4 rounded-xl border border-[#E5E0D8] space-y-2"
              >
                <h4 className="font-bold text-stone-900 text-sm">Complete Set Details</h4>
                <ul className="space-y-1 text-xs text-stone-700">
                  {completeSet.total_colors ? (
                    <li><strong className="text-stone-900">Colors:</strong> {completeSet.total_colors}</li>
                  ) : null}
                  {completeSet.taan_count ? (
                    <li><strong className="text-stone-900">Taan:</strong> {completeSet.taan_count}</li>
                  ) : null}
                  {completeSet.gaz_per_taan ? (
                    <li><strong className="text-stone-900">Gaz per Taan:</strong> {completeSet.gaz_per_taan}</li>
                  ) : null}
                  {totalGaz ? (
                    <li><strong className="text-stone-900">Total Gaz per Complete Set:</strong> {totalGaz}</li>
                  ) : null}
                  {product.retail_price ? (
                    <li><strong className="text-stone-900">Complete Set Price:</strong> Rs. {product.retail_price.toLocaleString()}</li>
                  ) : null}
                  <li>
                    <strong className="text-stone-900">Minimum Order:</strong>{" "}
                    {completeSet.min_order_sets || 1} Complete Set{(completeSet.min_order_sets || 1) === 1 ? "" : "s"}
                  </li>
                </ul>
                {completeSet.notes ? (
                  <p className="text-[11px] italic text-stone-500 whitespace-pre-wrap">{completeSet.notes}</p>
                ) : null}
                <p className="text-[10px] text-stone-500 pt-1">
                  Sold as a complete set — individual colors, Taans or Gaz cannot be purchased separately.
                </p>
              </div>
            ) : null}
          </div>
        )}

        {activeTab === "wholesale" && (
          <div className="space-y-4 text-xs sm:text-sm text-stone-700 leading-relaxed max-w-4xl">
            <p>
              Anwer Fancy Lace is a primary supplier to clothing brands, retail shops, and bridal fashion studios across Pakistan.
            </p>
            <div className="bg-amber-50/70 p-4 rounded-xl border border-amber-200 text-xs space-y-2">
              <p className="font-bold text-amber-900">Wholesale Terms for this product:</p>
              <p>• Wholesale Price: <strong className="text-amber-900">Rs. {product.wholesale_price?.toLocaleString()}</strong> per complete set</p>
              <p>• Minimum Order: <strong className="text-amber-900">{product.min_wholesale_qty || 5} Sets</strong> or 1 Full Factory Box</p>
              <p>• Custom Roll Yardage (25-50 Gaz) also available upon request.</p>
            </div>

            {Array.isArray(product.pricing_tiers) && product.pricing_tiers.length > 0 && (
              <div data-testid="product-pricing-tiers" className="bg-white p-4 rounded-xl border-2 border-[#D4AF37]/40 space-y-3">
                <h4 className="font-bold text-stone-900 flex items-center gap-1.5 text-xs uppercase tracking-wider">
                  <Sparkles className="w-3.5 h-3.5 text-[#D4AF37]" />
                  Bulk Wholesale Pricing Tiers
                </h4>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-wider text-stone-500 border-b border-stone-200">
                      <th className="pb-2">Sets Ordered</th>
                      <th className="pb-2">Price per Set</th>
                      <th className="pb-2 text-right">Savings vs Retail</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-100">
                    {[...product.pricing_tiers]
                      .sort((a, b) => Number(a.min_quantity) - Number(b.min_quantity))
                      .map((t, idx) => {
                        const savings = product.retail_price
                          ? Math.max(0, Math.round(((product.retail_price - Number(t.price_per_set)) / product.retail_price) * 100))
                          : 0;
                        return (
                          <tr key={idx} data-testid={`pricing-tier-row-${idx}`}>
                            <td className="py-2 font-semibold text-stone-800">
                              {t.label || `${t.min_quantity}+ Sets`}
                            </td>
                            <td className="py-2 font-bold text-[#996515]">
                              Rs. {Number(t.price_per_set).toLocaleString()}
                            </td>
                            <td className="py-2 text-right text-emerald-700 font-semibold">
                              {savings > 0 ? `-${savings}%` : "—"}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
                <p className="text-[10px] text-stone-500 italic">
                  Tier prices apply automatically for wholesale orders on this product.
                </p>
              </div>
            )}

            <Link
              to="/wholesale"
              className="inline-flex items-center gap-1.5 text-xs font-bold text-[#996515] underline"
            >
              Go to Wholesale Order Form →
            </Link>
          </div>
        )}

        {activeTab === "delivery" && (
          <div className="space-y-4 text-xs sm:text-sm text-stone-700 leading-relaxed max-w-4xl">
            <p>
              We deliver to all major cities and towns across Pakistan (Lahore, Karachi, Islamabad, Faisalabad, Rawalpindi, Multan, Peshawar, Quetta, Gujranwala, Sialkot, etc.).
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
              <div className="p-3 bg-[#FAF8F5] rounded-lg border border-stone-200">
                <strong className="text-stone-900 block mb-1">Standard Delivery Time:</strong>
                <span>2 - 4 working days via premier courier services.</span>
              </div>
              <div className="p-3 bg-[#FAF8F5] rounded-lg border border-stone-200">
                <strong className="text-stone-900 block mb-1">Payment Modes Accepted:</strong>
                <span>Cash on Delivery (COD), Meezan Bank Transfer, EasyPaisa & JazzCash.</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Related Sets */}
      {related.length > 0 && (
        <div className="space-y-6 pt-6">
          <div className="flex items-center justify-between">
            <h3 className="font-heading text-xl sm:text-2xl font-bold text-stone-900">
              More Designs from {product.category}
            </h3>
            <Link to={`/shop?category=${encodeURIComponent(product.category)}`} className="text-xs font-semibold text-[#996515] hover:underline">
              View All →
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {related.map((item) => (
              <ProductCard key={item.id} product={item} />
            ))}
          </div>
        </div>
      )}

      {/* Reviews */}
      <ReviewsSection productId={product.id} />

      {/* Modal */}
      {modalOpen && (
        <SetBreakdownModal product={product} onClose={() => setModalOpen(false)} />
      )}

    </div>
  );
}
